import { useTranslation } from 'react-i18next'
import { Circle, X, Pause, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { AttendanceStatus } from '@/api/attendance'
import { displayAttendanceStatus } from '@/utils/attendanceDisplay'

interface Props {
  status: AttendanceStatus | 'NONE' | null | undefined
  label?: string
  /** Dense lists use the compact chip; the palette stays the same. */
  size?: 'default' | 'sm'
}

type Variant = {
  Icon: LucideIcon
  iconFill?: string
  classes: string
  i18nKey: string
}

const VARIANTS: Record<AttendanceStatus, Variant> = {
  IN: {
    Icon: Circle,
    iconFill: 'currentColor',
    classes: 'bg-lc-mint-100 text-lc-mint-900',
    i18nKey: 'attendance.status.IN',
  },
  OUT: {
    Icon: Circle,
    classes: 'bg-lc-gray-100 text-lc-gray-700',
    i18nKey: 'attendance.status.OUT',
  },
  ABSENT: {
    Icon: X,
    classes: 'bg-lc-red-100 text-lc-red-900',
    i18nKey: 'attendance.status.ABSENT',
  },
  LEAVE: {
    Icon: Pause,
    classes: 'bg-lc-orange-100 text-lc-orange-900',
    i18nKey: 'attendance.status.LEAVE',
  },
}

export function AttendanceStatusChip({ status, label: labelOverride, size = 'default' }: Props) {
  const { t } = useTranslation('learner')
  const v = VARIANTS[displayAttendanceStatus(status)]
  const label = labelOverride ?? t(v.i18nKey)
  const Icon = v.Icon

  return (
    <span
      role="status"
      aria-label={label}
      className={cn(
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-full',
        size === 'sm' ? 'h-6 gap-1 px-2' : 'h-7 gap-1 px-3 py-1',
        'text-lc-caption-1 font-medium',
        v.classes,
      )}
    >
      {/* An explicit `fill={undefined}` would drop lucide's `fill="none"` and paint outline icons solid black. */}
      <Icon className={size === 'sm' ? 'size-3' : 'size-4'} {...(v.iconFill ? { fill: v.iconFill } : {})} aria-hidden />
      {label}
    </span>
  )
}
